/**
 * Valuations Metrics and Calculation Logic
 *
 * Defines completion calculation, status metrics, category determination,
 * and data availability checks for Valuations.
 */

// Helper function to get nested property values
const getNestedProperty = (obj, path) => {
  return path.split(".").reduce((current, prop) => current?.[prop], obj);
};

// Get valuation completion metrics
export const getValuationMetrics = (valuation) => {
  if (!valuation) return { completion: 0, completedFields: 0, totalFields: 0 };

  const requiredFields = [
    "name",
    "type",
    "status",
    "property.address",
    "valuation.marketValue",
    "valuer.firm",
    "dates.reported",
  ];

  const optionalFields = [
    "description",
    "purpose",
    "relatedProject",
    "relatedDeal",
    "property.tenure",
    "property.siteArea",
    "valuation.gdv",
    "valuation.profit",
    "valuer.surveyor",
    "methodology",
    "basis",
    "confidence",
  ];

  const allFields = [...requiredFields, ...optionalFields];

  const completedFields = allFields.filter((field) => {
    const value = getNestedProperty(valuation, field);
    return value !== null && value !== undefined && value !== "";
  }).length;

  const completion = Math.round((completedFields / allFields.length) * 100);

  const completedRequiredFields = requiredFields.filter((field) => {
    const value = getNestedProperty(valuation, field);
    return value !== null && value !== undefined && value !== "";
  }).length;

  return {
    completion,
    completedFields,
    totalFields: allFields.length,
    requiredFields: requiredFields.length,
    completedRequiredFields,
  };
};

// Calculate overall metrics for a list of valuations
export const calculateValuationsMetrics = (valuations = []) => {
  const total = valuations.length;

  const statusCounts = valuations.reduce((acc, valuation) => {
    const status = valuation.status || "draft";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const typeCounts = valuations.reduce((acc, valuation) => {
    const type = valuation.type || "market-valuation";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Calculate average completion
  const totalCompletion = valuations.reduce((sum, valuation) => {
    return sum + getValuationMetrics(valuation).completion;
  }, 0);

  const averageCompletion = total > 0 ? Math.round(totalCompletion / total) : 0;

  // Calculate total value of all valuations
  const totalValue = valuations.reduce((sum, valuation) => {
    const value =
      valuation.valuation?.marketValue ||
      valuation.valuation?.gdv ||
      valuation.valuation?.totalValue ||
      0;
    return sum + value;
  }, 0);

  // Calculate average valuation confidence
  const valuationsWithConfidence = valuations.filter((v) => v.confidence);
  const confidenceLevels = { high: 3, medium: 2, low: 1 };
  const averageConfidence =
    valuationsWithConfidence.length > 0
      ? Math.round(
          valuationsWithConfidence.reduce(
            (sum, v) => sum + (confidenceLevels[v.confidence] || 2),
            0
          ) / valuationsWithConfidence.length
        )
      : 2;

  return {
    total,
    draft: statusCounts.draft || 0,
    "under-review": statusCounts["under-review"] || 0,
    approved: statusCounts.approved || 0,
    expired: statusCounts.expired || 0,

    // Type breakdown
    "market-valuation": typeCounts["market-valuation"] || 0,
    "development-appraisal": typeCounts["development-appraisal"] || 0,
    "investment-valuation": typeCounts["investment-valuation"] || 0,
    "rental-valuation": typeCounts["rental-valuation"] || 0,

    // Financial metrics
    totalValue,
    averageCompletion,
    averageConfidence:
      ["low", "medium", "high"][averageConfidence - 1] || "medium",

    // Status percentages
    approvedPercentage:
      total > 0 ? Math.round(((statusCounts.approved || 0) / total) * 100) : 0,
    underReviewPercentage:
      total > 0
        ? Math.round(((statusCounts["under-review"] || 0) / total) * 100)
        : 0,
  };
};

// Determine valuation category for icons
export const getValuationCategory = (valuation) => {
  if (!valuation.type) return "market";

  const type = valuation.type.toLowerCase();

  if (type.includes("development")) return "development";
  if (type.includes("investment")) return "investment";
  if (type.includes("rental")) return "rental";
  return "market";
};

// Check data availability for a valuation
export const checkValuationDataAvailability = (valuation) => {
  const metrics = getValuationMetrics(valuation);

  return {
    hasBasicInfo: !!(valuation.name && valuation.type && valuation.status),
    hasProperty: !!valuation.property?.address,
    hasValuation: !!(
      valuation.valuation?.marketValue || valuation.valuation?.gdv
    ),
    hasValuer: !!valuation.valuer?.firm,
    hasDates: !!valuation.dates?.reported,
    hasRelatedRecords: !!(valuation.relatedProject || valuation.relatedDeal),
    completionLevel: metrics.completion,

    // Quality indicators
    isComplete: metrics.completion === 100,
    isWellFormed: metrics.completion >= 80,
    needsAttention: metrics.completion < 50,

    // Missing critical data
    missingCritical: metrics.completedRequiredFields < metrics.requiredFields,
  };
};

// Generate status summary for valuations
export const getValuationsStatusSummary = (valuations = []) => {
  const metrics = calculateValuationsMetrics(valuations);

  return [
    {
      label: "Total Valuations",
      value: metrics.total,
      type: "count",
      color: "blue",
    },
    {
      label: "Approved",
      value: metrics.approved,
      percentage: metrics.approvedPercentage,
      type: "status",
      color: "green",
    },
    {
      label: "Under Review",
      value: metrics["under-review"],
      percentage: metrics.underReviewPercentage,
      type: "status",
      color: "yellow",
    },
    {
      label: "Total Value",
      value: new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(metrics.totalValue),
      type: "currency",
      color: "indigo",
    },
  ];
};
